// ─── La bitácora: lo que le pasó a cada registro, en palabras ────────────────
// Cada guardado en la nube deja una fila en `public.auditoria` con el registro
// ANTES y DESPUÉS (src/core/auditLog.js), y los procesos que no son un
// guardado —emitir ante la DIAN, reenviar el correo, abrir el PDF, entrar a
// AURA, administrar usuarios— dejan un EVENTO. Este módulo convierte esas
// filas en lo que una persona quiere leer: quién, cuándo y qué cambió
// («Estado: Borrador → Enviada», «+ 100 × Libreta»), en vez de dos JSON.
//
// Es puro (sin estado ni DOM) para poderlo probar: los nombres de clientes y
// proveedores llegan en `ctx`.
import { repararTildes } from "./tildes.js";

// ─── Qué es cada entidad ─────────────────────────────────────────────────────
export const ENTIDADES = {
  cotizaciones: { label: "Cotización", icon: "📋", tipo: "CT" },
  pedidos: { label: "Orden de producción", icon: "🔧", tipo: "OP" },
  ocp: { label: "Orden de compra", icon: "🛒", tipo: "OCP" },
  requisiciones: { label: "Requisición (piloto)", icon: "📝" },
  recepciones: { label: "Recepción RP (piloto)", icon: "📥" },
  apartados: { label: "Apartado de inventario (piloto)", icon: "📌" },
  remisiones: { label: "Remisión", icon: "📮", tipo: "RC" },
  facturas: { label: "Factura de venta", icon: "💰", tipo: "FACT" },
  notas_credito: { label: "Nota crédito", icon: "🧾", tipo: "NC" },
  cuentas_pagar: { label: "Cuenta por pagar", icon: "📥" },
  cuentas_cobro: { label: "Cuenta de cobro", icon: "✍️" },
  asientos: { label: "Asiento contable", icon: "📒" },
  inventario: { label: "Inventario (kardex)", icon: "📦" },
  clientes: { label: "Cliente", icon: "👥" },
  proveedores: { label: "Proveedor", icon: "🏭" },
  productos: { label: "Producto", icon: "🏷️" },
  empleados: { label: "Empleado", icon: "🧑‍💼" },
  puc: { label: "Cuenta del PUC", icon: "🗂️" },
  resoluciones_dian: { label: "Resolución DIAN", icon: "🏛️" },
  nomina_liquidaciones: { label: "Liquidación de nómina", icon: "💵" },
  activos_fijos: { label: "Activo fijo", icon: "🏢" },
  alertas_enviadas: { label: "Alerta enviada", icon: "🔔" },
  configuracion: { label: "Configuración", icon: "⚙️" },
  consecutivos: { label: "Consecutivo", icon: "🔢" },
  usuarios: { label: "Usuario", icon: "👤" },
  facturas_recibidas: { label: "Factura recibida por correo", icon: "📥" },
  sesion: { label: "Sesión", icon: "🔐" }
};
export const etiquetaEntidad = (e) => (ENTIDADES[e] && ENTIDADES[e].label) || e || "—";

// Las configuraciones viven en tenant_config, una fila por clave.
export const CONFIGURACIONES = {
  "erp:alert_config": "Alertas",
  "erp:empresa_config": "Datos de la empresa",
  "erp:ia_config": "Asistente IA",
  "erp:parametros_fiscales": "Parámetros fiscales",
  "erp:parametros_nomina": "Parámetros de nómina",
  "erp:tabla_comisiones": "Tabla de comisiones",
  "erp:roles_personalizados": "Roles personalizados",
  "erp:puesta_en_marcha": "Puesta en marcha",
  "erp:splash_config": "Pantalla de bienvenida",
  "erp:politica_password": "Política de contraseñas",
  "erp:politica_sesion": "Política de sesión",
  "erp:roles_sistema": "Permisos de los roles",
  "erp:formato_documentos": "Formato de los documentos",
  "erp:dataico_config": "Facturación electrónica (Dataico)",
  "erp:costos_via": "Destino recordado de los costos"
};

// ─── Qué pasó ────────────────────────────────────────────────────────────────
// `accion` es create / update / delete / evento; un evento dice cuál en
// `evento`. Lo que no esté aquí se muestra con su clave.
export const EVENTOS = {
  emitir_dian: { label: "Emitió la factura ante la DIAN", icon: "🏛️", color: "#059669" },
  rechazo_dian: { label: "La DIAN rechazó la emisión", icon: "⛔", color: "#dc2626" },
  emitir_nc_dian: { label: "Emitió la nota crédito ante la DIAN", icon: "🏛️", color: "#059669" },
  rechazo_nc_dian: { label: "La DIAN rechazó la nota crédito", icon: "⛔", color: "#dc2626" },
  reenviar_correo: { label: "Reenvió el correo al cliente", icon: "📧", color: "#2563eb" },
  imprimir: { label: "Abrió el documento (PDF)", icon: "📄", color: "#64748b" },
  ingreso: { label: "Ingresó a AURA", icon: "🔓", color: "#059669" },
  salida: { label: "Cerró la sesión", icon: "🔒", color: "#64748b" },
  salida_inactividad: { label: "Sesión cerrada por inactividad", icon: "⏳", color: "#b45309" },
  salida_seguridad: { label: "Sesión cerrada por un cambio de seguridad", icon: "🛡️", color: "#b45309" },
  consecutivo_ajustado: { label: "Ajustó el consecutivo", icon: "🔢", color: "#b45309" },
  restaurar_respaldo: { label: "Restauró un respaldo", icon: "♻️", color: "#dc2626" },
  factura_recibida: { label: "Llegó por correo a la bandeja", icon: "📥", color: "#0369a1" },
  factura_descartada: { label: "Descartó la factura recibida", icon: "✕", color: "#b91c1c" },
  usuario_crear: { label: "Creó el usuario", icon: "➕", color: "#059669" },
  usuario_invitar: { label: "Invitó al usuario", icon: "✉️", color: "#059669" },
  usuario_cambiar_rol: { label: "Cambió el rol", icon: "🎭", color: "#b45309" },
  usuario_desactivar: { label: "Desactivó el usuario", icon: "⛔", color: "#dc2626" },
  usuario_reactivar: { label: "Reactivó el usuario", icon: "✅", color: "#059669" },
  usuario_forzar_cambio_password: { label: "Le exigió cambiar la contraseña", icon: "🔑", color: "#b45309" },
  usuario_restablecer_password: { label: "Le restableció la contraseña", icon: "🔑", color: "#b45309" },
  usuario_reiniciar_mfa: { label: "Le reinició la verificación en dos pasos", icon: "📱", color: "#b45309" },
  usuario_eliminar: { label: "Eliminó el usuario", icon: "🗑️", color: "#dc2626" }
};

export function etiquetaAccion(fila, entidad) {
  const e = entidad || (fila && fila.entidad);
  if (fila.accion === "evento") {
    const ev = EVENTOS[fila.evento] || {};
    return { label: ev.label || fila.evento || "Evento", icon: ev.icon || "•", color: ev.color || "#475569" };
  }
  if (e === "configuracion") {
    return fila.accion === "create" ? { label: "Configuró por primera vez", icon: "⚙️", color: "#059669" }
         : fila.accion === "delete" ? { label: "Borró la configuración", icon: "🗑️", color: "#dc2626" }
         : { label: "Cambió la configuración", icon: "⚙️", color: "#b45309" };
  }
  return fila.accion === "create" ? { label: "Creó", icon: "✨", color: "#059669" }
       : fila.accion === "delete" ? { label: "Eliminó", icon: "🗑️", color: "#dc2626" }
       : { label: "Editó", icon: "✏️", color: "#b45309" };
}

// ─── Cómo se llama un registro ───────────────────────────────────────────────
// Lo que una persona busca: el número del documento, el nombre del tercero, el
// código de la cuenta. Se guarda en la columna `referencia` para poder buscar
// «RS-0228» sin abrir cada JSON.
export function referenciaDe(entidad, datos) {
  if (!datos || typeof datos !== "object") return null;
  if (entidad === "configuracion") return CONFIGURACIONES[datos.__clave] || datos.__clave || null;
  if (entidad === "puc") return [datos.codigo, datos.nombre].filter(Boolean).join(" ") || null;
  if (entidad === "inventario") return datos.articulo || datos.nombre || null;
  if (entidad === "asientos") return datos.numero || datos.concepto || datos.descripcion || null;
  const r = datos.numero || datos.nombre || datos.razon_social || datos.codigo || datos.email || datos.articulo || null;
  return r == null ? null : String(r).slice(0, 200);
}

// ─── Aligerar lo que se guarda ───────────────────────────────────────────────
// La tabla llegó a 73 MB, 19 de ellos en 77 ediciones de productos: cada fila
// copiaba la foto entera en base64, dos veces (antes y después). Para la
// bitácora basta saber que la imagen cambió.
const MAX_TEXTO = 4000;
export const MARCA_ARCHIVO = "[archivo adjunto]";
export function aligerar(valor, prof = 0) {
  if (valor == null) return valor;
  if (typeof valor === "string") {
    if (/^data:[^;,]+[;,]/.test(valor) && valor.length > 200) return MARCA_ARCHIVO;
    if (valor.length > MAX_TEXTO) return valor.slice(0, MAX_TEXTO) + "…";
    return valor;
  }
  if (typeof valor !== "object") return valor;
  if (prof > 12) return "[…]";
  if (Array.isArray(valor)) return valor.map(v => aligerar(v, prof + 1));
  const salida = {};
  for (const k of Object.keys(valor)) salida[k] = aligerar(valor[k], prof + 1);
  return salida;
}

// ─── Qué cambió, campo por campo ─────────────────────────────────────────────
// Campos que se mueven solos o que no le dicen nada a nadie.
const IGNORAR = new Set([
  "id", "tenant_id", "updated_at", "created_at", "__clave", "_sync", "_rev", "_locked",
  "_creadoPor", "_creadoPorNombre", "posicion", "_tmp", "_abierto", "_expandido"
]);

export const CAMPOS = {
  numero: "Número", fecha: "Fecha", estado: "Estado", estado_pago: "Estado de pago",
  cliente_id: "Cliente", proveedor_id: "Proveedor", tercero_id: "Tercero",
  subtotal: "Subtotal", iva: "IVA", total: "Total", saldo: "Saldo", neto: "Neto",
  retefuente: "Retención en la fuente", reteica: "ReteICA", reteiva: "ReteIVA",
  notas: "Notas", observaciones: "Observaciones", items: "Líneas",
  condiciones_pago: "Condiciones de pago", forma_pago: "Forma de pago",
  asesor_comercial: "Asesor comercial", tiempo_entrega: "Tiempo de entrega",
  lugar_entrega: "Lugar de entrega", fecha_vigencia: "Vigencia", fecha_vencimiento: "Vencimiento",
  fecha_entrega: "Fecha de entrega", enviar_a: "Enviar a",
  _contacto: "Contacto", _clienteLibre: "Tercero no registrado", _origen: "Viene de",
  _origenes: "Viene de", _convertedTo: "Convertido a", _conceptosCosto: "Costos asociados",
  _costosLinea: "Costos de línea", _dias_vigencia: "Días de vigencia",
  _fecha_enviada: "Fecha de envío", _dataico: "Factura electrónica", _externa: "Emitida fuera de AURA",
  _asientoVenta: "Asiento de venta", _inventarioDescontado: "Inventario descontado",
  _aplicaciones: "Aplicaciones", _devoluciones: "Devoluciones", _cruzado: "Cruzado",
  _cerrada: "Cerrada", informativa: "Informativa", _abonos: "Abonos", abonos: "Abonos",
  nombre: "Nombre", razon_social: "Razón social", nit: "NIT / documento", documento: "Documento",
  dv: "DV", ciudad: "Ciudad", direccion: "Dirección", telefono: "Teléfono", email: "Correo",
  dias_pago: "Días de pago", cupo: "Cupo", _contactos: "Contactos", contactos: "Contactos",
  regimen: "Régimen", responsable_iva: "Responsable de IVA", declara_383: "Declara art. 383",
  tarifa_retefuente: "Tarifa de retención", tarifa_reteica: "Tarifa ReteICA",
  descuentos_financieros: "Descuentos financieros",
  codigo: "Código", codigo_interno: "Código interno", costo: "Costo", margen: "Margen",
  precio: "Precio", precio_venta: "Precio de venta", unidad: "Unidad", categoria: "Categoría",
  iva_tipo: "Régimen de IVA", iva_pct: "Tarifa de IVA", es_kit: "Es kit",
  _componentesKit: "Componentes del kit", imagen: "Imagen", foto: "Foto", logo: "Logo",
  descripcion: "Descripción", concepto: "Concepto", articulo: "Artículo", cantidad: "Cantidad",
  precio_unitario: "Precio unitario", movimientos: "Movimientos", _movimientos: "Movimientos",
  stock: "Existencias", costo_promedio: "Costo promedio", cargo: "Cargo", salario: "Salario",
  proximo_numero: "Próximo número", prefijo: "Prefijo", resolucion: "Resolución",
  payment_means: "Medio de pago", enviar_correo: "Enviar correo"
};

function etiquetaCampo(k) {
  if (CAMPOS[k]) return CAMPOS[k];
  const limpio = String(k).replace(/^_+/, "").replace(/_/g, " ");
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
}

const DINERO = /^(subtotal|iva|total|saldo|neto|valor|monto|costo|precio|retefuente|reteica|reteiva|salario|cupo|debito|credito|base)(_|$)|(_total|_valor|_costo|_precio|precio_unitario|costo_promedio)$/;
const fmtPesos = (n) => "$" + new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(Number(n) || 0);

const ESTADOS_TXT = {
  borrador: "Borrador", enviada: "Enviada", aprobada: "Aprobada", completada: "Completada",
  convertida: "Convertida", rechazada: "Rechazada", confirmada: "Confirmada", en_proceso: "En proceso",
  cancelada: "Cancelada", recibida: "Recibida", entregada: "Entregada", facturada: "Facturada",
  emitida: "Emitida", pagada: "Pagada", anulada: "Anulada", por_pagar: "Por pagar", pendiente: "Pendiente",
  parcial: "Parcial", vencida: "Vencida", abonada: "Abonada", causada: "Causada"
};

// Un valor, como lo leería una persona.
export function textoValor(k, v, ctx = {}) {
  if (v === undefined || v === null || v === "") return "—";
  if (v === MARCA_ARCHIVO || (typeof v === "string" && /^data:/.test(v))) return "imagen";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if ((k === "cliente_id" || k === "proveedor_id" || k === "tercero_id") && typeof v === "string") {
    const lista = k === "cliente_id" ? ctx.clientes : k === "proveedor_id" ? ctx.proveedores
      : [...(ctx.clientes || []), ...(ctx.proveedores || [])];
    const t = (lista || []).find(x => x.id === v);
    return t ? t.nombre : v;
  }
  if (/^estado/.test(k) && typeof v === "string") return ESTADOS_TXT[v] || v;
  if (typeof v === "number" || (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v)) && DINERO.test(k))) {
    return DINERO.test(k) ? fmtPesos(v) : String(v);
  }
  if (Array.isArray(v)) return `${v.length} ${v.length === 1 ? "elemento" : "elementos"}`;
  if (typeof v === "object") {
    if (v.numero) return String(v.numero);
    if (v.nombre) return String(v.nombre);
    if (k === "_dataico") return v.cufe ? `CUFE ${String(v.cufe).slice(0, 12)}…` : (v.real ? "Emitida" : "Registrada");
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  }
  const s = String(v).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return s.length > 120 ? s.slice(0, 120) + "…" : s;
}

const igual = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const vacio = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

// Cómo se nombra un elemento de una lista (una línea, un contacto, un costo).
function nombreElemento(x) {
  if (!x || typeof x !== "object") return String(x);
  const cant = x.cantidad != null && x.cantidad !== "" ? `${x.cantidad} × ` : "";
  const nombre = x.articulo || x.concepto || x.nombre || x.descripcion || x.cuenta || x.email || x.numero || "elemento";
  const precio = x.precio_unitario != null && x.precio_unitario !== "" ? ` (${fmtPesos(x.precio_unitario)})`
    : x.valor != null && x.valor !== "" ? ` (${fmtPesos(x.valor)})` : "";
  return `${cant}${textoValor("", nombre)}${precio}`;
}
const llaveElemento = (x, i) => (x && (x.id || x.ccid || x._ccid)) || `#${i}`;

// Diferencia de dos listas: lo que entró, lo que salió y lo que cambió adentro.
function diffLista(k, antes, despues, ctx) {
  const a = Array.isArray(antes) ? antes : [];
  const d = Array.isArray(despues) ? despues : [];
  const cambios = [];
  // Se emparejan por id/ccid; si no traen ninguno, por artículo; y si tampoco,
  // por posición.
  const conLlave = [...a, ...d].some(x => x && (x.id || x.ccid || x._ccid));
  const llave = conLlave ? llaveElemento
    : (x, i) => (x && (x.articulo || x.concepto || x.nombre || x.email)) ? String(x.articulo || x.concepto || x.nombre || x.email) + "|" + i : `#${i}`;
  const porLlaveA = new Map(a.map((x, i) => [llave(x, i), x]));
  const porLlaveD = new Map(d.map((x, i) => [llave(x, i), x]));
  // Sin llaves estables la posición empareja, así que «misma posición» es
  // «mismo elemento» aunque cambie el nombre.
  const emparejar = conLlave ? null : Math.min(a.length, d.length);
  if (emparejar != null) {
    for (let i = 0; i < emparejar; i++) cambios.push(...diffElemento(k, a[i], d[i], ctx));
    for (let i = emparejar; i < d.length; i++) cambios.push({ campo: k, tipo: "agrega", texto: "+ " + nombreElemento(d[i]) });
    for (let i = emparejar; i < a.length; i++) cambios.push({ campo: k, tipo: "quita", texto: "− " + nombreElemento(a[i]) });
    return cambios;
  }
  for (const [ll, x] of porLlaveD) {
    if (!porLlaveA.has(ll)) cambios.push({ campo: k, tipo: "agrega", texto: "+ " + nombreElemento(x) });
    else cambios.push(...diffElemento(k, porLlaveA.get(ll), x, ctx));
  }
  for (const [ll, x] of porLlaveA) {
    if (!porLlaveD.has(ll)) cambios.push({ campo: k, tipo: "quita", texto: "− " + nombreElemento(x) });
  }
  return cambios;
}

function diffElemento(k, a, d, ctx) {
  if (igual(a, d)) return [];
  if (!a || !d || typeof a !== "object" || typeof d !== "object") {
    return [{ campo: k, tipo: "cambia", texto: `${nombreElemento(a)} → ${nombreElemento(d)}` }];
  }
  const partes = [];
  for (const c of new Set([...Object.keys(a), ...Object.keys(d)])) {
    if (IGNORAR.has(c) || igual(a[c], d[c])) continue;
    if (vacio(a[c]) && vacio(d[c])) continue;
    partes.push(`${etiquetaCampo(c).toLowerCase()} ${textoValor(c, a[c], ctx)} → ${textoValor(c, d[c], ctx)}`);
  }
  if (!partes.length) return [];
  return [{ campo: k, tipo: "cambia", texto: `${nombreElemento(d)}: ${partes.join("; ")}` }];
}

// La lista de cambios entre dos versiones de un registro, ya en palabras.
// Cada cambio: { campo, etiqueta, tipo: "cambia"|"agrega"|"quita", texto }.
export function diffRegistro(antes, despues, ctx = {}) {
  const a = antes && typeof antes === "object" ? antes : {};
  const d = despues && typeof despues === "object" ? despues : {};
  const cambios = [];
  const claves = [...new Set([...Object.keys(a), ...Object.keys(d)])];
  // Primero lo que más se busca: estado, totales, tercero; después el resto.
  const orden = ["estado", "estado_pago", "numero", "fecha", "cliente_id", "proveedor_id", "total", "subtotal", "iva"];
  claves.sort((x, y) => {
    const ix = orden.indexOf(x), iy = orden.indexOf(y);
    return (ix < 0 ? 99 : ix) - (iy < 0 ? 99 : iy);
  });
  for (const k of claves) {
    if (IGNORAR.has(k)) continue;
    const va = a[k], vd = d[k];
    if (igual(va, vd) || (vacio(va) && vacio(vd))) continue;
    const etiqueta = etiquetaCampo(k);
    if (Array.isArray(va) || Array.isArray(vd)) {
      diffLista(k, va, vd, ctx).forEach(c => cambios.push({ ...c, etiqueta }));
      continue;
    }
    if (va && vd && typeof va === "object" && typeof vd === "object" && !va.numero && !vd.numero && !va.nombre && !vd.nombre) {
      // Un objeto anidado (la configuración de Dataico, el contacto…): se
      // abre un nivel para decir QUÉ cambió adentro.
      for (const c of new Set([...Object.keys(va), ...Object.keys(vd)])) {
        if (IGNORAR.has(c) || igual(va[c], vd[c]) || (vacio(va[c]) && vacio(vd[c]))) continue;
        cambios.push({ campo: k, etiqueta: `${etiqueta} · ${etiquetaCampo(c)}`, tipo: "cambia",
          texto: `${textoValor(c, va[c], ctx)} → ${textoValor(c, vd[c], ctx)}` });
      }
      continue;
    }
    if ((va === MARCA_ARCHIVO || vd === MARCA_ARCHIVO || /^data:/.test(String(va || "")) || /^data:/.test(String(vd || "")))) {
      cambios.push({ campo: k, etiqueta, tipo: "cambia", texto: vacio(va) ? "se cargó" : vacio(vd) ? "se quitó" : "se reemplazó" });
      continue;
    }
    cambios.push({ campo: k, etiqueta, tipo: "cambia", texto: `${textoValor(k, va, ctx)} → ${textoValor(k, vd, ctx)}` });
  }
  return cambios;
}

// Lo que dice un registro recién creado: lo esencial, no los 40 campos.
export function resumenCreacion(entidad, datos, ctx = {}) {
  if (!datos || typeof datos !== "object") return [];
  const sale = [];
  const poner = (k) => {
    if (vacio(datos[k])) return;
    sale.push({ campo: k, etiqueta: etiquetaCampo(k), tipo: "agrega", texto: textoValor(k, datos[k], ctx) });
  };
  ["estado", "fecha", "cliente_id", "proveedor_id", "total"].forEach(poner);
  if (datos._origen) sale.push({ campo: "_origen", etiqueta: "Viene de", tipo: "agrega", texto: textoValor("_origen", datos._origen, ctx) });
  if (Array.isArray(datos.items) && datos.items.length) {
    sale.push({ campo: "items", etiqueta: "Líneas", tipo: "agrega", texto: datos.items.map(nombreElemento).slice(0, 6).join(" · ") + (datos.items.length > 6 ? ` · y ${datos.items.length - 6} más` : "") });
  }
  if (!sale.length) ["nit", "ciudad", "codigo", "codigo_interno", "costo", "cantidad"].forEach(poner);
  return sale;
}

// ─── La línea de tiempo de un registro ───────────────────────────────────────
// El autoguardado de la cotización escribe cada pocos segundos: una tarde de
// trabajo son 50 filas. Se agrupan las ediciones SEGUIDAS de la misma persona
// separadas por menos de `ventanaMin` minutos en una sola entrada, cuyo cambio
// es el del primer «antes» al último «después».
export function lineaDeTiempo(filas, ctx = {}, ventanaMin = 15) {
  const ordenadas = [...(filas || [])].sort((x, y) => new Date(x.fecha) - new Date(y.fecha));
  const grupos = [];
  for (const f of ordenadas) {
    const ult = grupos[grupos.length - 1];
    const quien = f.usuario_email || f.usuario_nombre || "";
    if (ult && f.accion === "update" && ult.accion === "update" && ult.quien === quien &&
        ult.entidad === f.entidad && ult.entidadId === f.entidad_id &&
        new Date(f.fecha) - new Date(ult.hasta) <= ventanaMin * 60000) {
      ult.filas.push(f); ult.hasta = f.fecha; ult.despues = f.datos_despues;
      continue;
    }
    grupos.push({ accion: f.accion, evento: f.evento, entidad: f.entidad, entidadId: f.entidad_id,
      referencia: f.referencia || null, quien,
      usuario: f.usuario_nombre || f.usuario_email || "—", desde: f.fecha, hasta: f.fecha,
      antes: f.datos_antes, despues: f.datos_despues, filas: [f] });
  }
  const salida = grupos.map(g => {
    const cambios = g.accion === "update" ? diffRegistro(g.antes, g.despues, ctx)
      : g.accion === "create" ? resumenCreacion(g.entidad, g.despues, ctx)
      : g.accion === "evento" ? detalleEvento(g.despues)
      : [];
    return { ...g, cambios, veces: g.filas.length, ...etiquetaAccion(g.filas[0], g.entidad) };
  });
  // Un guardado que no cambió nada visible (el autoguardado sin teclear) no
  // le dice nada a nadie: se cuenta, pero no se muestra.
  const visibles = salida.filter(g => g.accion !== "update" || g.cambios.length > 0);
  return { entradas: visibles.reverse(), sinCambios: salida.length - visibles.length };
}

// Lo que trae un evento (el CUFE, el correo al que se reenvió…), en palabras.
export function detalleEvento(det) {
  if (!det || typeof det !== "object") return [];
  return Object.keys(det).filter(k => !IGNORAR.has(k) && !vacio(det[k])).map(k => ({
    campo: k, etiqueta: etiquetaCampo(k), tipo: "agrega", texto: repararTildes(textoValor(k, det[k]))
  }));
}

// Una sola línea, para la tabla de Auditoría.
export function resumenFila(fila, ctx = {}) {
  if (fila.accion === "evento") return detalleEvento(fila.datos_despues).map(c => `${c.etiqueta}: ${c.texto}`).slice(0, 3).join(" · ");
  if (fila.accion === "update") {
    const c = diffRegistro(fila.datos_antes, fila.datos_despues, ctx);
    if (!c.length) return "Guardado sin cambios de contenido";
    const txt = c.slice(0, 3).map(x => `${x.etiqueta}: ${x.texto}`).join(" · ");
    return c.length > 3 ? `${txt} · y ${c.length - 3} cambio${c.length - 3 === 1 ? "" : "s"} más` : txt;
  }
  if (fila.accion === "create") return resumenCreacion(fila.entidad, fila.datos_despues, ctx).slice(0, 3).map(x => `${x.etiqueta}: ${x.texto}`).join(" · ");
  return "";
}

// Una configuración puede traer credenciales viejas (la llave del asistente IA
// vivió un tiempo en el navegador): a la bitácora no llegan nunca.
const SECRETO = /^(api_?key|apikey|secret|client_secret|token|access_token|refresh_token|password|contrase)/i;
export function sinSecretos(valor, prof = 0) {
  if (!valor || typeof valor !== "object" || prof > 12) return valor;
  if (Array.isArray(valor)) return valor.map(v => sinSecretos(v, prof + 1));
  const salida = {};
  for (const k of Object.keys(valor)) {
    if (SECRETO.test(k)) continue;
    salida[k] = sinSecretos(valor[k], prof + 1);
  }
  return salida;
}

// Lo que se manda al evento de un usuario: NUNCA la contraseña.
export function detalleSeguro(obj) {
  if (!obj || typeof obj !== "object") return null;
  const salida = {};
  for (const k of Object.keys(obj)) {
    if (/pass|clave|secret|token|key/i.test(k)) continue;
    if (k === "accion" || k === "redirectTo") continue;
    salida[k] = obj[k];
  }
  return Object.keys(salida).length ? salida : null;
}
